import styled, { css } from 'styled-components';
import { EBalancesTheme } from 'utils/enums';

interface StyledHeaderProps {
  backgroundColor?: string;
  visibility?: string;
  isProfileInfo?: boolean;
  hasBadge?: boolean;
  noLogo?: boolean;
  balanceTheme?: EBalancesTheme;
}

const logoStyles = (props: StyledHeaderProps) => css`
  width: ${props.isProfileInfo ? 'auto' : '128px'};
  height: ${props.isProfileInfo ? '60px' : 'auto'};
  margin: ${props.isProfileInfo ? '0' : 'auto'};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  ${props.hasBadge && !props.noLogo && css`
    position: static;
    left: 50%;
  `}

  &__main {
    width: ${props.isProfileInfo ? 'unset' : '100%'};
    height: ${props.isProfileInfo ? '100%' : 'unset'};
    object-fit: contain;
  }

  &__sub {
    width: 150%;
  }

  &.big {
    width: ${props.isProfileInfo ? 'auto' : '176px'};
  }
`;

const profileContainerStyles = (props: StyledHeaderProps) => css`
  margin-bottom: ${props.balanceTheme === EBalancesTheme.PLAY_SIMPLE ? '16px' : '0'};
  cursor: pointer;
  height: ${props.isProfileInfo ? 'fit-content' : '44px'};
  display: ${props.isProfileInfo ? 'flex' : 'block'};
  align-items: ${props.isProfileInfo ? 'center' : 'none'};
  flex: ${props.hasBadge && props.isProfileInfo ? 'none' : props.isProfileInfo ? '1' : '0'};

  .frame {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${props.isProfileInfo ? '40px' : '44px'};
    width: ${props.isProfileInfo ? '40px' : '44px'};
    border-radius: 50%;
    margin-right: 5px;
  }

  &__profileFrame {
    position: absolute;
    height: ${props.isProfileInfo ? '36px' : '44px'};
    width: ${props.isProfileInfo ? '36px' : '44px'};
  }

  &__img {
    height: 90%;
    border-radius: 50%;
    background-color: gray;
  }

  &-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: fit-content;
    flex: 1;
    gap: 2px;
    margin-left: 4px;
    color: white;
    font-size: 12px;
    line-height: normal;
    font-weight: 400;

    & > div:nth-child(2) {
      align-items: flex-start;
    }
  }

  .bagelCodeInfoContainer {
    margin-left: 4px;
  }
`;

const badgesContainerStyles = (props: StyledHeaderProps) => css`
  display: flex;
  gap: 15px;
  position: ${props.isProfileInfo ? 'static' : 'absolute'};
  right: 0;

  @media only screen and (max-width: 767px) {
    justify-content: flex-end;
    margin-top: 5px;
    position: static;
    width: ${props.noLogo ? '100%' : 'auto !important'};
  }
`;

const landscapeTabletStyles = css<StyledHeaderProps>`
  @media only screen and (orientation: landscape) and (max-width: 1280px) {
    padding-top: 20px;
    margin: 0 24px;

    .defaultInfoLogo {
      ${props => props.hasBadge ? css`
        left: ${props.noLogo ? '50%' : 'auto !important'};
        right: ${props.noLogo ? 'auto !important' : '34px'};
        transform: ${props.noLogo ? 'translate(-50%)' : 'translate(0) !important'};
        margin-top: ${props.hasBadge ? '10px' : '0'};
      ` : css`
        position: static;
        left: 0;
        transform: translateX(0);
        margin-top: 0;
      `}
    }

    .logo {
      ${props => props.hasBadge && props.isProfileInfo && css`
        position: static !important;
        transform: translateX(0) !important;
      `}
    }

    .badgesContainer {
      display: flex;
      gap: 15px;
      position: static;
      right: 0;
      justify-content: flex-end;
      margin-top: 5px;
      width: ${props => props.noLogo ? '100%' : 'auto !important'};
    }
  }
`;

const tabletStyles = css<StyledHeaderProps>`
  @media only screen and (min-width: 768px) {
    padding-right: 0;

    .header-content {
      margin: 0 10px;
    }

    &.pipa {
      .profileContainer {
        height: 36px;
        width: 36px;

        .frame {
          height: 36px;
          width: 36px;
        }
      }
    }

    &.play-simple {
      .profileContainer {
        height: 32px;
        width: 100%;

        .frame {
          height: 32px;
          width: 32px;
        }
      }
    }

    .profileContainer {
      height: ${props => props.isProfileInfo ? 'fit-content' : '60px'};
      width: ${props => props.isProfileInfo ? 'fit-content' : '60px'};

      .frame {
        height: ${props => props.isProfileInfo ? '48px' : '60px'};
        width: ${props => props.isProfileInfo ? '48px' : '60px'};
      }

      &-info {
        font-size: 1rem;
        margin-left: 8px;
      }

      &__profileFrame {
        height: ${props => props.isProfileInfo ? '48px' : '60px'};
        width: ${props => props.isProfileInfo ? '48px' : '60px'};
      }
    }

    .logo {
      width: ${props => props.isProfileInfo ? 'auto' : '180px'};
      height: ${props => props.isProfileInfo ? '64px' : 'auto'};

      &.big {
        width: 176px;
      }
    }
  }
`;

const largeDesktopStyles = css<StyledHeaderProps>`
  @media only screen and (min-width: 1024px) {
    .logo {
      width: ${props => props.isProfileInfo ? 'auto' : '196px'};
      height: ${props => props.isProfileInfo ? '64px' : 'auto'};

      &.big {
        width: 220px;
      }
    }
  }
`;

const extraLargeDesktopStyles = css<StyledHeaderProps>`
  @media only screen and (min-width: 1280px) {
    max-width: 1248px;

    &.play-simple {
      max-width: auto;
    }

    &.pipa {
      max-width: auto;
      margin-bottom: 12px;

      .profileContainer {
        &-info {
          font-size: 16px;
        }
      }
    }
  }
`;

const mediumDesktopStyles = css<StyledHeaderProps>`
  @media only screen and (min-width: 849px) and (max-width: 1279px) {
    &.pipa {
      max-width: auto;
    }
  }
`;

const mobileStyles = css<StyledHeaderProps>`
  @media only screen and (max-width: 767px) {
    height: ${props => props.isProfileInfo ? 'fit-content' : props.hasBadge ? '170px' : 'auto'};
    display: flex;
    flex-direction: column;

    .header-content {
      &.play-simple {
        position: relative;
        flex-direction: column;

        .play-simple--badge {
          position: static;
          transform: translate(0);
        }
      }
    }
      
    ${props => !props.noLogo && !props.hasBadge ? css`
      .defaultInfoLogo,
      .logo {
        position: static;
        margin: 0;
      }

      .header-content {
        justify-content: space-between;
        align-items: center;
        flex-direction: row;
      }
    ` : css`
      .defaultInfoLogo {
        position: absolute !important;
        right: 34px;
        left: ${props.noLogo && !props.hasBadge ? '50%' : 'auto !important'};
        right: ${props.noLogo ? 'auto !important' : '34px !important'};
        transform: ${props.noLogo ? 'translate(-50%)' : 'translate(0) !important'};
        margin-top: ${props.hasBadge ? '5px' : '0'};
      }

      .header-content {
        margin: 0 10px;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;

        ${props.hasBadge && !props.noLogo && css`
          position: ${props.isProfileInfo ? 'relative' : 'absolute !important'};
          justify-content: space-between !important;
          left: 50%;
          transform: translate(-50%);
        `}

        .badgesContainer {
          justify-content: flex-start;
          margin-top: 10px;
        }

        .logo {
          ${props.isProfileInfo && props.hasBadge && !props.noLogo && css`
              position: absolute;
              right: 0;
            `
          }
        }
      }
    `}
  }
`;

// Main StyledHeader component
export const StyledHeader = styled.div<StyledHeaderProps>`
  display: flex;
  justify-content: center;
  width: 100%;
  background: ${props => props.backgroundColor || 'transparent'};
  visibility: ${props => props.visibility || 'visible'};

  &.play-simple {
    position: sticky;
    backdrop-filter: blur(3px);
    top: 0;
    z-index: 10;
    margin-bottom: 28px;
  }

  .header-content {
    font-family: var(--font-family);
    display: flex;
    min-height: 84px;
    height: fit-content;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding: 1em;
    position: relative;
    z-index: 1;
    margin: 0 auto;
    box-sizing: border-box;

    &.play-simple {
      min-height: auto;
      padding: 0;
      margin: 16px auto 10px;
      flex-direction: row;
      align-items: baseline;

      .profileContainer {
        width: 100%;
        height: 32px;
        margin-bottom: 0;
        flex-direction: row;

        &__profileFrame {
          width: 32px;
          height: 32px;
        }

        .frame {
          margin-right: 5px;
          height: 32px;
          width: 32px;

          .profileContainer__img {
            height: 100%;
          }
        }

        &-info {
          margin-left: 2px;
        }
      }

      .play-simple--badge {
        height: 24px;
        object-fit: contain;
        margin: 6px auto;
        position: absolute;
        left: 50%;
        transform: translate(-50%);
      }
    }

    &.pipa {
      min-height: auto;
      padding: 0;
      margin: 40px 16px 6px 16px;
      width: calc(100% - 32px);
      max-width: auto;

      .profileContainer {
        height: 36px;
        width: 36px;

        .frame {
          height: 36px;
          width: 36px;
        }

        &-info {
          margin-left: 3px;
          flex-direction: row;
          color: #fff;
          align-items: center;
          justify-content: start;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
          gap: 0;

          .playerName {
            margin-right: 8px;
          }
        }
      }
    }

    .logo {
      ${logoStyles}
    }

    .profileContainer {
      ${profileContainerStyles}
    }

    .badgesContainer {
      ${badgesContainerStyles}
    }
  }

  // All Media Queries
  ${landscapeTabletStyles}
  ${tabletStyles}
  ${mobileStyles}
  ${largeDesktopStyles}
  ${extraLargeDesktopStyles}
  ${mediumDesktopStyles}
`;